export interface PageSuggestion {
  /** i18n key for the suggestion chip display text (localized via t()) */
  labelKey: string;
  /**
   * The question text sent to the AI backend when clicked.
   * Intentionally kept in English because the AI backend processes
   * questions in English regardless of the user's UI language.
   * The `labelKey` handles display localization for the suggestion chip.
   */
  question: string;
}

/**
 * Map of page URL patterns to contextual suggestions.
 * Keys are substrings matched against the current URL pathname.
 */
const PAGE_SUGGESTIONS: Record<string, PageSuggestion[]> = {
  screenManagement: [
    {
      labelKey: 'optibot.suggestion.gettingStarted',
      question: 'How do I get started with OptiSigns?',
    },
    {
      labelKey: 'optibot.suggestion.splitScreens',
      question: 'How do I set up split screens and zones?',
    },
    {
      labelKey: 'optibot.suggestion.screenOffline',
      question: 'My screen is showing offline, how do I fix it?',
    },
  ],
  assetManagement: [
    { labelKey: 'optibot.suggestion.uploadAssets', question: 'How do I upload and manage files?' },
    {
      labelKey: 'optibot.suggestion.assignContent',
      question: 'How do I assign content to a screen?',
    },
    {
      labelKey: 'optibot.suggestion.setLiveExpire',
      question: 'How do I set asset live and expire dates?',
    },
  ],
  playlistManagement: [
    {
      labelKey: 'optibot.suggestion.createPlaylist',
      question: 'How do I create and use playlists?',
    },
    { labelKey: 'optibot.suggestion.nestedPlaylist', question: 'How do nested playlists work?' },
    {
      labelKey: 'optibot.suggestion.playbackControl',
      question: 'How do I control playlist item playback duration?',
    },
  ],
  scheduleManagement: [
    {
      labelKey: 'optibot.suggestion.createSchedule',
      question: 'How do I create and use schedules?',
    },
    {
      labelKey: 'optibot.suggestion.defaultContent',
      question: 'How do I set default content on schedules?',
    },
    {
      labelKey: 'optibot.suggestion.tvPowerSchedule',
      question: 'How do I schedule TV power on/off?',
    },
  ],
  templateManagement: [
    {
      labelKey: 'optibot.suggestion.useTemplates',
      question: 'How do I use templates to create content?',
    },
    {
      labelKey: 'optibot.suggestion.optiSync',
      question: 'How does OptiSync work for auto-updating content?',
    },
    {
      labelKey: 'optibot.suggestion.designerQR',
      question: 'How do I add a QR code in the Designer?',
    },
  ],
};

const DEFAULT_SUGGESTIONS: PageSuggestion[] = [
  {
    labelKey: 'optibot.suggestion.gettingStarted',
    question: 'How do I get started with OptiSigns?',
  },
  { labelKey: 'optibot.suggestion.howToScreens', question: 'How do I manage my screens?' },
  { labelKey: 'optibot.suggestion.howToContent', question: 'How do I create and assign content?' },
];

/**
 * Get contextual suggestions based on the current page URL.
 */
export function getPageSuggestions(pathname: string): PageSuggestion[] {
  for (const [pattern, suggestions] of Object.entries(PAGE_SUGGESTIONS)) {
    if (pathname.includes(pattern)) {
      return suggestions;
    }
  }
  return DEFAULT_SUGGESTIONS;
}
